import { auth as firebaseAuth } from "../../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  Unsubscribe,
  updateEmail,
  updateProfile,
  User,
} from "firebase/auth";

import { getErrorCode } from "./FirebaseRepository/FirebaseUtils";
import { AuthException } from "../exceptions/AuthException";
import UserModel from "../models/UserModel";
import { pageDomain, rubkNameProduct } from "../../utils";

class AuthRepository {
  get currentUser(): UserModel | null {
    const user = firebaseAuth.currentUser;
    return user ? this.convertToUserModel(user) : null;
  }

  get currentUserId(): string {
    if (!firebaseAuth.currentUser) {
      throw new Error("User is not defined!");
    }

    return firebaseAuth.currentUser.uid;
  }

  get isAuthenticated(): boolean {
    return firebaseAuth.currentUser ? true : false;
  }

  signInWithEmail(email: string, password: string): Promise<boolean> {
    return signInWithEmailAndPassword(firebaseAuth, email, password)
      .then((userCredencial) => {
        return Boolean(userCredencial.user);
      })
      .catch((error) => {
        throw new AuthException(
          this.getError(error, AuthException.codes.LOGIN_ERROR)
        );
      });
  }

  async signOutUser(): Promise<void> {
    if (!this.isAuthenticated) return;
    return signOut(firebaseAuth).catch((error) => {
      throw new AuthException(
        this.getError(error, AuthException.codes.LOGIN_ERROR)
      );
    });
  }

  /**
     Esta funcao cria um usuario no Authentication do Firebase, envia o email 
     de verificacao e cria uma colecao para o mesmo no Firestore.
   * @param name string
   * @param email string
   * @param password string
   * @returns User - com os dados do usuario que acabou de ser criado
   */
  async createAccountWithEmail(
    name: string,
    email: string,
    password: string
  ): Promise<UserModel> {
    try {
      const userCredencial = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );

      if (userCredencial.user) {
        await updateProfile(userCredencial.user, { displayName: name });
        await sendEmailVerification(userCredencial.user);
      }

      return this.convertToUserModel(userCredencial.user);
    } catch (error) {
      throw new AuthException(
        this.getError(error, AuthException.codes.REGISTER_ERROR)
      );
    }
  }

  async updateCurrentUserEmail(newEmail: string) {
    const user = firebaseAuth.currentUser;
    if (!user) return;
    return updateEmail(user, newEmail).catch((error) => {
      throw new AuthException(
        this.getError(error, AuthException.codes.INTERNAL_ERROR)
      );
    });
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    const actionCodeSettings = {
      url: `https://${pageDomain}/${rubkNameProduct}`,
    };

    return sendPasswordResetEmail(
      firebaseAuth,
      email,
      actionCodeSettings
    ).catch((error) => {
      throw new AuthException(
        this.getError(error, AuthException.codes.PASSWORD_RESET_ERROR)
      );
    });
  }

  async sendVerificationEmailToCurrentUser() {
    const user = firebaseAuth.currentUser;
    if (!user) return;

    const actionCodeSettings = {
      url: `https://${pageDomain}/${rubkNameProduct}`,
    };
    return sendEmailVerification(user, actionCodeSettings).catch((error) => {
      throw new AuthException(
        this.getError(error, AuthException.codes.INTERNAL_ERROR)
      );
    });
  }

  subscribeAuthObserver(onChange: Function, onError?: Function): Unsubscribe {
    return firebaseAuth.onAuthStateChanged(
      (userAuthenticated) => {
        const user = userAuthenticated
          ? this.convertToUserModel(userAuthenticated)
          : null;
        onChange(user);
      },
      (error) => {
        if (onError) {
          onError(error);
        }
      }
    );
  }

  private getError(error: any, defaultCode: string): string {
    return getErrorCode("auth", defaultCode, error);
  }

  private convertToUserModel(user: User): UserModel {
    return new UserModel(
      user.uid,
      user.displayName || "",
      user.email || "",
      user.metadata.creationTime || "",
      user.metadata.lastSignInTime || "",
      user.refreshToken,
      user.emailVerified,
      user.isAnonymous,
      user.providerId
    );
  }
}

export default new AuthRepository();
